import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { Icon } from 'Components';
import { useIsMobile } from 'Hooks';

import styles from './PublishedFilterToolbar.module.scss';

export type SortOrder = 'asc' | 'desc';
export type ViewMode = 'grid' | 'list';

export interface LanguageOption {
  id: number;
  name: string;
}

interface PublishedFilterToolbarProps {
  categoryTitle: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedLanguageId: number | null;
  onLanguageChange: (languageId: number | null) => void;
  sortOrder: SortOrder | undefined;
  onSortChange: (order: SortOrder) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  languages?: LanguageOption[];
}

export const PublishedFilterToolbar: React.FC<PublishedFilterToolbarProps> = ({
  categoryTitle,
  searchQuery,
  onSearchChange,
  selectedLanguageId,
  onLanguageChange,
  sortOrder,
  onSortChange,
  viewMode,
  onViewModeChange,
  languages = []
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  const handleSearchInput = (value: string) => {
    setLocalSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedValue = value.trim();

    if (trimmedValue.length >= 3 || trimmedValue.length === 0) {
      debounceTimerRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 500);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const languageOptions = useMemo(
    () => [
      { label: t('phishingTemplateManager.publishedFilterToolbar.allLanguages'), value: '' },
      ...languages.map((lang) => ({
        label: lang.name,
        value: lang.id
      }))
    ],
    [languages, t]
  );

  const sortOptions = useMemo(
    () => [
      { label: t('phishingTemplateManager.publishedFilterToolbar.sortAZ'), value: 'asc' },
      { label: t('phishingTemplateManager.publishedFilterToolbar.sortZA'), value: 'desc' }
    ],
    [t]
  );

  return (
    <div className={`${styles.toolbarContainer} ${isMobile ? styles.mobile : ''}`}>
      <div className={styles.headingWrapper}>
        <div className={styles.iconBadge}>
          <Icon type="list" />
        </div>
        <span className={styles.categoryTitle}>{categoryTitle}</span> 
      </div>

      <div className={`${styles.controlsWrapper} ${isMobile ? styles.mobile : ''}`}>
        <div className={`${styles.searchBox} ${isMobile ? styles.mobile : ''}`}>
          <input
            type="text"
            placeholder={t('phishingTemplateManager.searchPlaceholder')}
            value={localSearch}
            onChange={(e) => handleSearchInput(e.target.value)} 
            className={styles.searchInput}
          />
          <div className={styles.searchIconWrapper}>
            <Icon type="search" /> 
          </div>
        </div>

        <div className={`${styles.filtersAndToggleGroup} ${isMobile ? styles.mobile : ''}`}>
          <div className={`${styles.languageSelectWrapper} ${isMobile ? styles.mobile : ''}`}>
            <Select
              className="custom-filter-select campaign-setup-select"
              dropdownClassName="campaign-setup-filter-option-select"
              suffixIcon={<Icon type="chevronDown" />}
              value={selectedLanguageId ?? ''}
              onChange={(value) => {
                onLanguageChange(value === '' ? null : Number(value)); 
              }}
              options={languageOptions}
              getPopupContainer={(trigger) => trigger.parentElement || document.body} 
              style={{ width: '100%', height: '36px' }} 
            />
          </div>

          <div className={`${styles.sortSelectWrapper} ${isMobile ? styles.mobile : ''}`}>
            <Select
              className="custom-filter-select campaign-setup-select"
              dropdownClassName="campaign-setup-filter-option-select"
              suffixIcon={<Icon type="arrowDownArrowUp" />}
              placeholder={t('phishingTemplateManager.publishedFilterToolbar.sortAZ', 'Sort: A-Z')}
              value={sortOrder}
              onChange={(value) => onSortChange(value as SortOrder)} 
              options={sortOptions}
              getPopupContainer={(trigger) => trigger.parentElement || document.body} 
              style={{ width: '100%', height: '36px' }} 
            />
          </div>

          <div className={styles.viewModeToggleWrapper}>
            <button
              type="button"
              onClick={() => onViewModeChange('grid')} 
              className={`${styles.viewModeButton} ${viewMode === 'grid' ? styles.active : ''}`}
              title={t('phishingTemplateManager.publishedFilterToolbar.gridView')}
            >
              <Icon type="grid" />
            </button>

            <button
              type="button"
              onClick={() => onViewModeChange('list')} 
              className={`${styles.viewModeButton} ${viewMode === 'list' ? styles.active : ''}`}
              title={t('phishingTemplateManager.publishedFilterToolbar.listView')}
            >
              <Icon type="list" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishedFilterToolbar;